import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as endpoints from "@/api/endpoints";
import type { Order } from "@/api/types";
import { apiErrorMessage } from "@/api/client";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime, formatYen } from "@/utils/format";

export function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await endpoints.fetchOrders(page, 20);
        if (!cancelled) {
          setOrders(res.orders);
          setTotalPages(res.pagination.total_pages || 1);
        }
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, "発注履歴の取得に失敗しました"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">発注履歴</h1>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">読み込み中...</div>
      ) : orders.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-600 mb-3">発注履歴がありません。</p>
          <Link to="/catalog" className="btn-primary inline-block">
            カタログを見る
          </Link>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">発注番号</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">発注日時</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">合計</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">ステータス</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{o.order_number}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDateTime(o.created_at)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatYen(o.total_amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/orders/${o.id}`} className="text-brand-700 hover:underline">
                      詳細
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            前へ
          </button>
          <div className="text-sm text-gray-600">
            {page} / {totalPages}
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            次へ
          </button>
        </div>
      )}
    </div>
  );
}
